import { Box, LinearProgress, Typography } from "@mui/material";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Parse from "parse";
import { useRevalidator } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CaseContext, useCase } from "../../context/CaseContext";
import { adjustedCase } from "../../shared/adjustedView";
import { Case } from "../../shared/CaseModel";
import { percentage } from "../../shared/percentage";
import { EvMatrix } from "./EvMatrix";
import { SegmentedControl } from "../general/SegmentedControl";

type View = "litigation" | "adjusted";

const ViewContext = createContext<{
  view: View;
  setView: (view: View) => void;
}>({ view: "litigation", setView: () => {} });

/** Keeps the view chosen while the user moves from page to page of a case. */
export const ResultsViewProvider = ({ children }: { children: ReactNode }) => {
  const [view, setView] = useState<View>("litigation");
  const value = useMemo(() => ({ view, setView }), [view]);
  return <ViewContext.Provider value={value}>{children}</ViewContext.Provider>;
};

/** The chances of a party where the settlement-adjusted results are shown. */
export const useAdjustedParty = (partyId: string) => {
  const { view } = useContext(ViewContext);
  const context = useContext(CaseContext);
  if (view !== "adjusted") return undefined;
  const adjusted = context.getCase().settlementAdjusted;
  return adjusted?.status === "done"
    ? adjusted.parties.find((party) => party.partyId === partyId)
    : undefined;
};

/** How often a page asks how far a job calculating the adjusted results is. */
const REFRESH_MS = 1_000;
/** A job ends within this, its own time limit; then nobody waits any more. */
const REFRESH_FOR_MS = 600_000;

/**
 * Asks every second, unless the last question is still out, until the job's
 * time is up. Returns what stops it.
 */
export function refreshEvery(
  ask: () => void,
  idle: () => boolean,
  since: number,
): () => void {
  const timer = setInterval(() => {
    if (Date.now() - since > REFRESH_FOR_MS) clearInterval(timer);
    else if (idle()) ask();
  }, REFRESH_MS);
  return () => clearInterval(timer);
}

/** How far a calculation is, as a share; undefined while that is not known. */
export const shareDone = (
  progress: { done: number; total: number } | undefined,
): number | undefined =>
  progress && progress.total > 0 ? progress.done / progress.total : undefined;

/**
 * While a job calculates the adjusted results, asks every second how far it
 * is, which is light, and loads the case again once they are there. Returns
 * how far it is.
 */
export function useRefreshWhilePending(): number | undefined {
  const { revalidate } = useRevalidator();
  const c = useContext(CaseContext).getCase();
  const pending = c.settlementAdjusted?.status === "pending";
  const [since] = useState(() => Date.now());
  const [share, setShare] = useState<number>();
  useEffect(() => {
    if (!pending) return;
    // asks no more once it has loaded the case again for the results
    let asking = false;
    let loaded = false;
    return refreshEvery(
      () => {
        asking = true;
        Parse.Cloud.run("getAdjustedProgress", { caseId: c.id })
          .then(
            ({
              status,
              done,
              total,
            }: {
              status?: string;
              done?: number;
              total?: number;
            }) => {
              if (status !== "pending") {
                loaded = true;
                void revalidate();
                return;
              }
              // between the last world and storing the results, the job
              // tells nothing: what it told last stands
              const told = shareDone(
                done !== undefined && total !== undefined
                  ? { done, total }
                  : undefined,
              );
              if (told !== undefined) setShare(told);
            },
          )
          .catch(() => {})
          .finally(() => (asking = false));
      },
      () => !asking && !loaded,
      since,
    );
  }, [pending, revalidate, since, c.id]);
  return pending ? share : undefined;
}

/** What a page says of adjusted results it cannot show. */
export const notShownKey = (
  adjusted: NonNullable<Case["settlementAdjusted"]>,
) =>
  adjusted.status === "failed" && adjusted.tooLarge
    ? "tooLarge"
    : adjusted.status;

/** What a page says of adjusted results it cannot show, with how far a job is. */
export const NotShown = ({
  adjusted,
  share,
  keyPrefix,
}: {
  adjusted: NonNullable<Case["settlementAdjusted"]>;
  share: number | undefined;
  keyPrefix: string;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix });
  return (
    <Box sx={{ maxWidth: 420 }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {t(notShownKey(adjusted))}
        {share !== undefined && ` ${percentage.format(share)}`}
      </Typography>
      {adjusted.status === "pending" && (
        <LinearProgress
          variant={share === undefined ? "indeterminate" : "determinate"}
          value={(share ?? 0) * 100}
          aria-label={t("pending")}
          sx={{ mt: 0.75, borderRadius: 1 }}
        />
      )}
    </Box>
  );
};

/** Litigation or settlement-adjusted: there only where a case has both. */
export const ResultsSwitch = () => {
  const { t } = useTranslation(undefined, { keyPrefix: "case.shownResults" });
  const { view, setView } = useContext(ViewContext);
  const adjusted = useCase().getCase().settlementAdjusted;
  const share = useRefreshWhilePending();
  if (!adjusted) return null;
  if (adjusted.status !== "done")
    return (
      <Box sx={{ mb: 1.5 }}>
        <NotShown adjusted={adjusted} share={share} keyPrefix="case.shownResults" />
      </Box>
    );
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
      <SegmentedControl
        ariaLabel={t("label")}
        value={view}
        onChange={setView}
        options={[
          { value: "litigation", label: t("litigation") },
          { value: "adjusted", label: t("adjusted") },
        ]}
      />
    </Box>
  );
};

/** What is under it reads the case in the chosen view, every figure of it. */
export const ShownResults = ({ children }: { children: ReactNode }) => {
  const { view } = useContext(ViewContext);
  const context = useContext(CaseContext);
  const value = useMemo(() => {
    if (view !== "adjusted") return context;
    const c = adjustedCase(context.getCase());
    const { results } = c;
    if (!results) return context;
    const evMatrix = new EvMatrix(results.evs);
    return {
      ...context,
      getCase: () => c,
      getOutcomes: () => results.outcomes,
      getEvMatrix: () => evMatrix,
      getPartyStats: (partyId: string) =>
        results.partyStats?.find((stats) => stats.partyId === partyId),
    };
  }, [context, view]);
  return <CaseContext.Provider value={value}>{children}</CaseContext.Provider>;
};
